import React, { useState, useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { formatDistanceToNow } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { Link, useNavigate } from 'react-router-dom';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import firebase from '../../config/firebase';

export default function NotificationMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const unsubscribeRefs = useRef([]);

  useEffect(() => {
    const db = firebase.firestore();

    // Busca todos os fretes primeiro
    const unsubscribeFreights = db.collection('freight')
      .onSnapshot(async (freightSnapshot) => {
        // Limpa listeners anteriores
        unsubscribeRefs.current.forEach(unsubscribe => {
          if (unsubscribe) unsubscribe();
        });
        unsubscribeRefs.current = [];

        // Para cada frete, cria um listener na subcoleção queue
        freightSnapshot.docs.forEach(freightDoc => {
          const freightId = freightDoc.id;
          const freightData = freightDoc.data();
          
          // Verifica se o status do frete é "P. Contratação" ou "Pendente de contratação"
          const freightStatus = freightData?.status?.describe || '';
          const isPendingContract = freightStatus === 'P.Contratação' || 
                                   freightStatus === 'Pendente de contratação';
          
          // Só cria listener para fretes com status pendente de contratação
          if (!isPendingContract) {
            return;
          }

          const unsubscribeQueue = db.collection('freight')
            .doc(freightId)
            .collection('queue')
            .onSnapshot((queueSnapshot) => {
              setNotifications(prev => {
                // Remove notificações antigas deste frete
                const filtered = prev.filter(notif => notif.freightId !== freightId);
                
                // Adiciona novas notificações da fila
                const newNotifications = [];
                queueSnapshot.docs.forEach(queueDoc => {
                  const queueData = queueDoc.data();
                  const isRead = queueData.isRead === true;
                  
                  // Só adiciona notificações que não foram lidas (isRead !== true)
                  if (!isRead) {
                    const origin = freightData?.firstDelivery?.city || 'Origem não informada';
                    const destination = freightData?.lastDelivery?.city || 'Destino não informado';
                    
                    newNotifications.push({
                      id: `${freightId}_${queueDoc.id}`,
                      queueDocId: queueDoc.id,
                      freightId: freightId,
                      freightNumber: freightData?.numberSerial || freightId,
                      driverName: queueData.driver_name || 'Motorista',
                      driverId: queueData.driver_user || queueData.uid || queueDoc.id || null,
                      origin: origin,
                      destination: destination,
                      title: `${queueData.driver_name?.split(' ')[0] + ' ' + queueData.driver_name?.split(' ')[1] || 'Motorista'} entrou na fila!`,
                      description: `${origin} → ${destination}`,
                      date: queueData.timestamp || new Date(),
                      read: isRead,
                    });
                  }
                });

                return [...filtered, ...newNotifications];
              });
            }, (error) => {
              console.error('Erro ao buscar fila:', error);
            });

          unsubscribeRefs.current.push(unsubscribeQueue);
        });
      }, (error) => {
        console.error('Erro ao buscar fretes:', error);
      });

    // Cleanup quando o componente desmontar
    return () => {
      unsubscribeFreights();
      unsubscribeRefs.current.forEach(unsubscribe => {
        if (unsubscribe) unsubscribe();
      });
      unsubscribeRefs.current = [];
    };
  }, []);



  const unreadCount = notifications.filter(n => !n.read).length;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notificationId) => {
    // Encontrar a notificação clicada
    const notification = notifications.find(notif => notif.id === notificationId);
    
    if (notification) {
      setSelectedNotification(notification);
      setOpenModal(true);
      handleClose();
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedNotification(null);
  };

  const handleMarkAsRead = async () => {
    if (!selectedNotification) return;
    
    const db = firebase.firestore();
    
    if (selectedNotification.freightId && selectedNotification.queueDocId) {
      try {
        // Atualizar a flag isRead no Firebase
        await db.collection('freight')
          .doc(selectedNotification.freightId)
          .collection('queue')
          .doc(selectedNotification.queueDocId)
          .update({
            isRead: true
          });
        
        // Remover a notificação da lista local (já que só mostramos não lidas)
        setNotifications(prev =>
          prev.filter(notif => notif.id !== selectedNotification.id)
        );
      } catch (error) {
        console.error('Erro ao marcar notificação como lida:', error);
      }
    }
    
    handleCloseModal();
  };

  const handleDriverClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedNotification?.driverId) {
      navigate(`/driverList/${selectedNotification.driverId}`);
      handleCloseModal();
    }
  };

  const handleFreightClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedNotification?.freightId) {
      navigate(`/freight/${selectedNotification.freightId}`);
      handleCloseModal();
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return formatDistanceToNow(dateObj, { addSuffix: true, locale: ptBR });
    } catch (error) {
      return '';
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        style={{ color: '#FFF' }}
        aria-label="notifications"
        aria-controls={open ? 'notification-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <NotificationsNoneOutlinedIcon color="#FFF" />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="notification-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'hidden',
            filter: 'drop-shadow(0px 8px 24px rgba(0,0,0,0.12))',
            mt: 1.5,
            minWidth: 380,
            maxWidth: 420,
            borderRadius: '12px',
            border: '1px solid rgba(0,0,0,0.08)',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              borderLeft: '1px solid rgba(0,0,0,0.08)',
              borderTop: '1px solid rgba(0,0,0,0.08)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '12px',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              bgcolor: '#fafafa',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#222',
                m: 0,
              }}
            >
              Notificações
            </Typography>
          </Box>

          {/* Notifications List */}
          <Box
            sx={{
              maxHeight: notifications.length > 5 ? '400px' : 'none',
              overflowY: notifications.length > 5 ? 'auto' : 'visible',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '3px',
                '&:hover': {
                  background: '#a8a8a8',
                },
              },
            }}
          >
            {notifications.length === 0 ? (
              <Box
                sx={{
                  px: 3,
                  py: 4,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#717171',
                    fontSize: '14px',
                  }}
                >
                  Nenhuma notificação no momento
                </Typography>
              </Box>
            ) : (
              notifications.map((notification, index) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification.id)}
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: index < notifications.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                    bgcolor: notification.read ? 'transparent' : '#f7f7f7',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: notification.read ? '#fafafa' : '#f0f0f0',
                    },
                    '&::before': notification.read ? 'none' : {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      bgcolor: '#1976d2',
                      borderRadius: '0 2px 2px 0',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: 'rgb(255,49,0)',
                      mr: 2,
                      flexShrink: 0,
                    }}
                  >
                    <LocalShippingIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: notification.read ? 500 : 600,
                        fontSize: '14px',
                        color: '#222',
                        mb: 0.5,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {truncateText(notification.title, 200)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '13px',
                        color: '#717171',
                        mb: 0.5,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {truncateText(notification.description, 500)}
                    </Typography>
                    {notification.date && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '11px',
                          color: '#b0b0b0',
                          display: 'block',
                        }}
                      >
                        {formatTimeAgo(notification.date)}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))
            )}
          </Box>
        </Box>
      </Menu>

      {/* Modal de Detalhes da Notificação */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedNotification && (
            <Box>
              {/* Header do Modal */}
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#fafafa',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#222',
                  }}
                >
                  Detalhes da Notificação
                </Typography>
                <IconButton
                  onClick={handleCloseModal}
                  size="small"
                  sx={{
                    color: '#717171',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Conteúdo do Modal */}
              <Box sx={{ p: 3 }}>
                {/* Nome do Motorista */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#717171',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 1,
                      display: 'block',
                    }}
                  >
                    Motorista
                  </Typography>
                  <Box
                    component={selectedNotification.driverId ? Link : Box}
                    to={selectedNotification.driverId ? `/driverList/${selectedNotification.driverId}` : '#'}
                    onClick={handleDriverClick}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: '8px',
                      bgcolor: '#f7f7f7',
                      cursor: selectedNotification.driverId ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': selectedNotification.driverId ? {
                        bgcolor: '#f0f0f0',
                        transform: 'translateY(-1px)',
                      } : {},
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'rgb(255,49,0)',
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: selectedNotification.driverId ? '#1976d2' : '#222',
                        '&:hover': selectedNotification.driverId ? {
                          textDecoration: 'underline',
                        } : {},
                      }}
                    >
                      {selectedNotification.driverName}
                    </Typography>
                  </Box>
                </Box>

                {/* Número do Frete */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#717171',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 1,
                      display: 'block',
                    }}
                  >
                    Número do Frete
                  </Typography>
                  <Box
                    component={Link}
                    to={`/freight/${selectedNotification.freightId}`}
                    onClick={handleFreightClick}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      borderRadius: '8px',
                      bgcolor: '#f7f7f7',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': {
                        bgcolor: '#f0f0f0',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <LocalShippingIcon sx={{ color: 'rgb(255,49,0)', fontSize: 24 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#1976d2',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {selectedNotification.freightNumber}
                    </Typography>
                  </Box>
                </Box>

                {/* Origem e Destino */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#717171',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 1.5,
                      display: 'block',
                    }}
                  >
                    Rota
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '8px',
                      bgcolor: '#f7f7f7',
                      border: '1px solid rgba(0,0,0,0.06)',
                    }}
                  >
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '11px',
                          color: '#717171',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Origem
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '16px',
                          fontWeight: 500,
                          color: '#222',
                        }}
                      >
                        {selectedNotification.origin}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: '1px',
                        bgcolor: 'rgba(0,0,0,0.1)',
                        my: 1.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '11px',
                          color: '#717171',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Destino
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '16px',
                          fontWeight: 500,
                          color: '#222',
                        }}
                      >
                        {selectedNotification.destination}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Botão de Fechar */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      color: '#717171',
                      '&:hover': {
                        bgcolor: '#f0f0f0',
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Fechar
                    </Typography>
                  </IconButton>
                  <IconButton
                    onClick={handleMarkAsRead}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      bgcolor: 'rgb(255,49,0)',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: 'rgb(230,44,0)',
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Marcar como lida
                    </Typography>
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


